import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { BiDotsVerticalRounded, BiSolidPencil } from "react-icons/bi";
import { FaCircle, FaMapMarkerAlt } from "react-icons/fa";
import { FiClock, FiMessageCircle, FiVideo } from "react-icons/fi";
import { TbCalendarBolt, TbCalendarRepeat, TbExternalLink } from "react-icons/tb";
import { useAppointmentMeetingLinkAction } from "@/components/appointment-meeting-link-button";
import { CarePlanBadge } from "@/components/care-plan/care-plan-badge";
import { CLEANUP_TIME_DEFAULT_COLOR } from "@/components/custom-calendar/cleanup-time-slice";
import { PREP_TIME_DEFAULT_COLOR } from "@/components/custom-calendar/prep-time-slice";
import { getAppointmentEditHref, getAppointmentRebookHref } from "@/components/custom-calendar/utils";
import { useProgressNavigation } from "@/components/progress-bar";
import AiScribeConsentDialog from "@/components/scribe/ai-scribe-consent-dialog";
import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui";
import CollapsibleText from "@/components/ui/collapsible-text";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useBookables } from "@/hooks/use-bookables";
import { useLocationClock } from "@/hooks/use-clinic-clock";
import { useEventTitle } from "@/hooks/use-event-title";
import { useRooms } from "@/hooks/use-rooms";
import { calculateClinicalInterval, formatCurrency, formatDate, formatList, formatName, formatStaffName, formatTime, formatToHumanReadable } from "@/lib/format-data";
import { PERMISSIONS, hasAnyPermission, hasPermission } from "@/lib/permissions";
import { addonDisplayName, groupServiceAddons, isAddonParent } from "@/lib/service-addon-groups";
import { getAppointmentTotalDisplay } from "@/lib/service-fees";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { UserType } from "@/types/enums";
import { EventStatus } from "@/types/enums/event";
import { InviteStatus } from "@/types/enums/invite-status";
import { MessageType } from "@/types/enums/message";
import { CreateNoteState } from "@/types/enums/note-state";
import { EventCleanupSegmentTiny, EventDetail, EventPrepSegmentTiny, getBookableSegments, getCleanupSegment, getPrepSegment, getServiceSegments } from "@/types/event";
import { AttributionLine } from "./attribution-line";
import EventLogHistory from "./event-log-history";
import { EventMessageList } from "./event-message-list";
import { declineTelehealthScribe, startTelehealthVisit } from "@/api/provider/telehealth";
import { updatePatientInCache } from "@/api/provider/user/patient/patient-cache-utils";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { eventQueryOptions, useUpdateEventMutation } from "@/query/check-in/calendar/event-query";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useScribeStore } from "@/stores/scribe-store";
function ServiceProviderLine(t0) {
  const $ = _c(7);
  const {
    service
  } = t0;
  let t1;
  if ($[0] !== service.providerId) {
    t1 = service.providerId ? formatStaffName(service.providerId) : undefined;
    $[0] = service.providerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const t2 = service.service?.name;
  let t3;
  if ($[2] !== name) {
    t3 = name ? <span>{`, with ${name}`}</span> : <span>{""}</span>;
    $[2] = name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <p className="text-caption leading-snug text-foreground-secondary">{t2}{t3}</p>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function AddonLine(t0) {
  const $ = _c(11);
  const {
    addon,
    parent
  } = t0;
  let t1;
  if ($[0] !== addon.providerId || $[1] !== parent.providerId) {
    t1 = addon.providerId && addon.providerId !== parent.providerId ? formatStaffName(addon.providerId) : null;
    $[0] = addon.providerId;
    $[1] = parent.providerId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const name = t1;
  const t2 = addon.service?.name;
  const t3 = parent.service?.name;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = addonDisplayName(t2, t3);
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `+ ${t4}`;
  let t6;
  if ($[6] !== name) {
    t6 = name ? <span>{`, with ${name}`}</span> : null;
    $[6] = name;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <p className="text-caption leading-snug text-foreground-secondary">{t5}{t6}</p>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function PrepTimeProviderLine(t0) {
  const $ = _c(27);
  const {
    prepTime,
    timeZone
  } = t0;
  let t1;
  if ($[0] !== prepTime.providerId) {
    t1 = prepTime.providerId ? formatStaffName(prepTime.providerId) : undefined;
    $[0] = prepTime.providerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerName = t1;
  const t2 = prepTime.color ? prepTime.color : PREP_TIME_DEFAULT_COLOR;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="h-3 w-3 flex-shrink-0 rounded-sm" style={{
      backgroundColor: t2
    }} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== prepTime.title) {
    t4 = prepTime.title || <span>{"Prep Time"}</span>;
    $[4] = prepTime.title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== providerName) {
    t5 = providerName ? <span>{`, with ${providerName}`}</span> : <span>{""}</span>;
    $[6] = providerName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== prepTime.start || $[9] !== timeZone) {
    t6 = formatTime(prepTime.start, true, timeZone);
    $[8] = prepTime.start;
    $[9] = timeZone;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span>{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span> - </span>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== prepTime.end || $[15] !== timeZone) {
    t9 = formatTime(prepTime.end, true, timeZone);
    $[14] = prepTime.end;
    $[15] = timeZone;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <span>{t9}</span>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t4 || $[21] !== t5 || $[22] !== t7) {
    t11 = <p className="text-caption leading-snug text-foreground-secondary">{t4}{t5}{" \xB7 "}{t7}{t8}{t10}</p>;
    $[19] = t10;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t3) {
    t12 = <>{t3}{t11}</>;
    $[24] = t11;
    $[25] = t3;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
function CleanupTimeProviderLine(t0) {
  const $ = _c(27);
  const {
    cleanupTime,
    timeZone
  } = t0;
  let t1;
  if ($[0] !== cleanupTime.providerId) {
    t1 = cleanupTime.providerId ? formatStaffName(cleanupTime.providerId) : undefined;
    $[0] = cleanupTime.providerId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerName = t1;
  const t2 = cleanupTime.color ? cleanupTime.color : CLEANUP_TIME_DEFAULT_COLOR;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="h-3 w-3 flex-shrink-0 rounded-sm" style={{
      backgroundColor: t2
    }} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== cleanupTime.title) {
    t4 = cleanupTime.title || <span>{"Cleanup"}</span>;
    $[4] = cleanupTime.title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== providerName) {
    t5 = providerName ? <span>{`, with ${providerName}`}</span> : <span>{""}</span>;
    $[6] = providerName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== cleanupTime.start || $[9] !== timeZone) {
    t6 = formatTime(cleanupTime.start, true, timeZone);
    $[8] = cleanupTime.start;
    $[9] = timeZone;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span>{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span> - </span>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== cleanupTime.end || $[15] !== timeZone) {
    t9 = formatTime(cleanupTime.end, true, timeZone);
    $[14] = cleanupTime.end;
    $[15] = timeZone;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <span>{t9}</span>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t4 || $[21] !== t5 || $[22] !== t7) {
    t11 = <p className="text-caption leading-snug text-foreground-secondary">{t4}{t5}{" \xB7 "}{t7}{t8}{t10}</p>;
    $[19] = t10;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t3) {
    t12 = <>{t3}{t11}</>;
    $[24] = t11;
    $[25] = t3;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
interface AppointmentItemProps {
  appointment: EventDetail;
  showStatus?: boolean;
  showDetailedTime?: boolean;
  showDate?: boolean;
  className?: string;
  showEdit?: boolean;
  showStatusDropdown?: boolean;
  onStatusChange?: (appointment: EventDetail, newStatus: EventStatus) => void;
}
export function AppointmentItem({
  appointment,
  showStatus = false,
  showDetailedTime = false,
  showDate = true,
  className,
  showEdit = false,
  showStatusDropdown = false,
  onStatusChange
}: AppointmentItemProps) {
  const closeSidebar = useRightSidebarStore(state => state.close);
  // The date and time line, and the prep, cleanup and room lines under it,
  // read the appointment's location clock, as its checklists do: a 12:30 AM
  // New York visit is that day, wherever the viewer.
  const clock = useLocationClock(appointment.location?.id);
  const {
    bookableMap
  } = useBookables();
  const {
    rooms,
    roomMap
  } = useRooms();
  const updateEvent = useUpdateEventMutation();
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const getEventTitle = useEventTitle();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    canShareMeetingLink,
    handleShareMeetingLink,
    isPending: isGeneratingMeetingLink
  } = useAppointmentMeetingLinkAction(appointment);
  const [isStartingVisit, setIsStartingVisit] = useState(false);
  const [isConsentDialogOpen, setIsConsentDialogOpen] = useState(false);
  const [isSavingDecline, setIsSavingDecline] = useState(false);
  // A decline only ever records No for a patient who hadn't agreed.
  const [undecidedPatientIds, setUndecidedPatientIds] = useState<string[]>([]);
  const {
    data: eventMessageTypes
  } = useQuery<MessageType[] | null>({
    ...eventQueryOptions.messageTypes(appointment.id)
  });
  const hydrateScribeStoreForTelehealth = (noteId: string) => {
    const store = useScribeStore.getState();
    store.setRecordingId(noteId);
    store.setAudioSource("telehealth");
    store.setRecordingNoteState(CreateNoteState.RECORDING);
  };
  const locationRooms = rooms.filter(r => r.locationId === appointment.location?.id);
  const patient = appointment.attendees.find(a => a.user.type === UserType.PATIENT)?.user;
  // Everyone on the call is recorded, so consent is asked for all of them.
  const patients = appointment.attendees.filter(a_0 => a_0.user.type === UserType.PATIENT && a_0.inviteStatus !== InviteStatus.REJECTED).map(a_1 => a_1.user);
  const meetingLink = appointment.meetingLink;
  // By video: at a virtual location, or a virtual-service visit at a clinic.
  const byVideo = !!(appointment.isVirtual ?? appointment.location?.isVirtual);
  const canStartTelehealth = !!meetingLink && byVideo && hasAnyPermission(currentUser, [PERMISSIONS.TELEHEALTH_VIDEO_USE]) && (tenant?.enabledModules.includes("RECALL") ?? false) && [EventStatus.PENDING, EventStatus.CONFIRMED, EventStatus.RESERVED].includes(appointment.status);
  const handleJoinMeeting = async (confirmedPatientIds: string[] = []) => {
    if (!meetingLink) return;

    // Without scribe permission, treat this like a plain meeting link.
    if (!canStartTelehealth) {
      // A running mic recording mustn't pick up a video visit's call.
      const store_0 = useScribeStore.getState();
      if (byVideo && store_0.isActive) store_0.pauseAudio();
      window.open(meetingLink, "_blank", "noopener,noreferrer");
      return;
    }
    if (!patient) {
      toast({
        title: "No patient assigned",
        description: "Add a patient to this appointment before starting.",
        variant: "error"
      });
      return;
    }
    const providerId = appointment.attendees.find(a_2 => a_2.user.type === UserType.PROVIDER)?.user.id;
    if (!providerId) {
      toast({
        title: "No provider assigned",
        description: "Add a provider to this appointment before starting.",
        variant: "error"
      });
      return;
    }
    setIsStartingVisit(true);
    try {
      if (tenant?.aiScribeConsentCheckEnabled && !confirmedPatientIds.length) {
        // Consent can change on another screen; don't trust a cached Yes.
        const records = await Promise.all(patients.map(p => queryClient.fetchQuery({
          ...patientQueryOptions.detail(p.id),
          staleTime: 0
        }).catch(() => null)));
        const undecided = patients.filter((_, i) => records[i]?.aiScribeConsent !== true).map(p_0 => p_0.id);
        if (undecided.length) {
          setUndecidedPatientIds(undecided);
          setIsConsentDialogOpen(true);
          return;
        }
      }
      const result = await startTelehealthVisit({
        eventId: appointment.id,
        providerId,
        patientId: patient.id,
        confirmedPatientIds
      });
      if (!result) {
        toast({
          title: "Could not start video visit",
          description: "Please try again.",
          variant: "error"
        });
        return;
      }
      // A fresh Join supersedes any earlier visit whose hydration is still
      // pending (its bot hadn't been admitted yet). Clear it up front so a
      // delayed TELEHEALTH_BOT_STATUS for that old bot can't later hijack the
      // PIP away from the visit being started here. The new-session branch
      // below re-arms it for this bot.
      useScribeStore.getState().setPendingTelehealthHydration(undefined);
      for (const id of confirmedPatientIds) {
        updatePatientInCache(id, {
          aiScribeConsent: true
        });
      }
      if (result.botId === null) {
        // No scribe bot: Recall is off, or a patient said No to AI scribe.
        // Open it as a plain video visit, without a running mic recording
        // picking up the call.
        const store_1 = useScribeStore.getState();
        if (store_1.isActive) store_1.pauseAudio();
      } else if (result.noteId) {
        // Seed the scribe store so the global PIP appears immediately —
        // useTelehealthRecordingSync would otherwise only fire after the
        // playground/note query resolves.
        hydrateScribeStoreForTelehealth(result.noteId);
      } else {
        // New/rejoined session: the bot is spawned but hasn't been admitted,
        // so its Note doesn't exist yet — the webhook will create it and
        // publish the id via TELEHEALTH_BOT_STATUS. Arm hydration in the store
        // (not local state) keyed on THIS bot's id, so `useTelehealthHydration`
        // in the global PIP seeds the slice when this exact bot is admitted —
        // not some sibling/stale Note on the same event.
        const store_2 = useScribeStore.getState();
        if (store_2.isActive) {
          // A mic recording is running. Finalize it FIRST so its audio isn't
          // lost: pauseAudio stops the recorder and lets recorder.onstop enqueue
          // the final chunk with the mic recording context captured at stop-time,
          // then halts further chunks so none get misrouted to the telehealth
          // note once hydration takes over the single global recording slice.
          // Do NOT use stopAudio here — it clears currentRecordingId before
          // onstop fires and can drop that final chunk (see scribe-store).
          store_2.pauseAudio();
        } else if (store_2.audioSource === "telehealth" && store_2.recordingNoteState !== CreateNoteState.RECORDING) {
          // The slice is holding a telehealth note that isn't live — a prior,
          // ended visit's note. isActive is always false for telehealth (only
          // the mic path sets it), so it can't tell a live visit from a dead
          // one; recordingNoteState can, so a RECORDING bot is left untouched
          // here rather than silently dropped from the PIP. Drop both the id
          // AND the "telehealth" source: clearing the id alone would strand
          // audioSource on "telehealth", and a later mic startAudio would then
          // take the telehealth-resume path and bail on the missing id. The PIP
          // hides until the new bot is admitted; telehealth has no client-side
          // chunks, so nothing is lost.
          store_2.setRecordingId(undefined);
          store_2.setAudioSource(undefined);
        }
        store_2.setPendingTelehealthHydration({
          eventId: appointment.id,
          recallBotId: result.botId
        });
      }
      window.open(meetingLink, "_blank", "noopener,noreferrer");
      navigateWithProgress(`/patient/${patient.id}`);
    } catch (e) {
      toast({
        title: "Could not start video visit",
        description: (e as Error)?.message ?? "Unknown error",
        variant: "error"
      });
    } finally {
      setIsStartingVisit(false);
    }
  };
  const handleConsentDeclined = async () => {
    if (!patient || !meetingLink) return;
    setIsSavingDecline(true);
    try {
      const result_0 = await declineTelehealthScribe(appointment.id, undecidedPatientIds);
      // An empty response (e.g. an aborted request) doesn't prove the bot is gone.
      if (!result_0) throw new Error("No response");
      // Same as the Join path: don't let a running mic recording pick up the call.
      const store_3 = useScribeStore.getState();
      if (store_3.isActive) store_3.pauseAudio();
      // An earlier Join's bot may still be waiting to be admitted; don't let its
      // late status alert switch the recording window back on.
      if (store_3.pendingTelehealthHydration?.eventId === appointment.id) {
        store_3.setPendingTelehealthHydration(undefined);
      }
      for (const id_0 of result_0.declinedPatientIds) {
        updatePatientInCache(id_0, {
          aiScribeConsent: false
        });
      }
      setIsConsentDialogOpen(false);
      toast({
        title: "Joining without AI scribe",
        description: "The patient declined AI scribe, so this visit won't be recorded."
      });
      window.open(meetingLink, "_blank", "noopener,noreferrer");
    } catch {
      toast({
        title: "Error",
        description: "Couldn't take AI scribe out of this visit. Try again before joining.",
        variant: "error"
      });
    } finally {
      setIsSavingDecline(false);
    }
  };
  return <div className={className}>
      <AiScribeConsentDialog open={isConsentDialogOpen} onOpenChange={setIsConsentDialogOpen} patientName={formatList(patients.map(p_1 => formatName(p_1.firstName, p_1.lastName)))} onAgreed={() => {
      setIsConsentDialogOpen(false);
      void handleJoinMeeting(undecidedPatientIds);
    }} onDeclined={() => void handleConsentDeclined()} isDeclining={isSavingDecline} />
      {/* Title with appointment actions */}
      <div className="flex items-center justify-between">
        <p className="text-body-lg font-medium leading-tight text-foreground">
          {getEventTitle(appointment)}
        </p>
        {showEdit && <div className="flex items-center">
            {/* Non-modal: renders inside a Dialog on the patient Appointments
                tab, where a modal menu aria-hides the rest of the page. */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="p-1 text-muted-foreground hover:text-primary" aria-label="Appointment actions" title="Appointment actions">
                  <BiDotsVerticalRounded className="size-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem icon={<BiSolidPencil />} onSelect={() => {
              closeSidebar(patientSidebarDefinition.id);
              navigateWithProgress(getAppointmentEditHref(appointment.id));
            }}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem icon={<TbCalendarRepeat />} onSelect={() => {
              closeSidebar(patientSidebarDefinition.id);
              navigateWithProgress(getAppointmentRebookHref(appointment));
            }}>
                  Rebook
                </DropdownMenuItem>
                <DropdownMenuItem icon={<TbCalendarBolt />} disabled={!canShareMeetingLink || isGeneratingMeetingLink} onSelect={handleShareMeetingLink}>
                  {isGeneratingMeetingLink ? "Generating…" : "Generate Google Calendar link"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>}
      </div>

      {/* Care Plan Badge */}
      {appointment.carePlan && <div className="mb-2">
          <CarePlanBadge carePlan={appointment.carePlan} />
        </div>}

      {/* When - Date/Time */}
      {appointment.start && <div className="mt-1">
          <p className="text-caption leading-snug text-foreground-secondary">
            {(() => {
          const parts = [];
          const appointmentDate = new Date(appointment.start);
          const zone = clock.timeZone;
          const isToday = clock.dayOf(appointment.start) === clock.today();
          const isFuture = appointmentDate > new Date();
          const day = formatDate(appointment.start, {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: zone
          });
          if (showDate) {
            parts.push(day);
          } else if (isFuture && !isToday) {
            // In sidebar view, show actual date for future appointments
            parts.push(day);
          }
          if (showDetailedTime) {
            const timeRange = appointment.end ? `${formatTime(appointment.start, true, zone)} - ${formatTime(appointment.end, true, zone)}` : formatTime(appointment.start, true, zone);
            parts.push(timeRange);
          }

          // Always show relative duration
          parts.push(calculateClinicalInterval(appointment.start));
          return parts.join(" · ");
        })()}
          </p>
        </div>}

      {/* Location and Meeting Link */}
      {(appointment.location || appointment.meetingLink) && <div className="mt-1 flex items-center gap-1 text-caption leading-snug text-foreground-secondary">
          {appointment.location &&
      // A virtual-service visit is booked at a clinic, but the patient
      // joins by video, so the clinic isn't where to send them.
      <div className="flex items-center gap-1">
              {byVideo ? <FiVideo className="h-3 w-3 text-muted-foreground" /> : <FaMapMarkerAlt className="h-3 w-3 text-muted-foreground" />}
              <span>
                {byVideo ? "Virtual Meeting" : appointment.location.name}
              </span>
            </div>}

          {appointment.location && appointment.meetingLink && <FaCircle className="h-1 w-1 text-foreground-muted" />}

          {meetingLink && <button type="button" onClick={() => void handleJoinMeeting()} disabled={isStartingVisit} className={cx(clickableText, "flex items-center gap-1 text-caption disabled:opacity-50")}>
              Join Meeting
              <TbExternalLink className="h-3 w-3" />
            </button>}
        </div>}

      {/* Room quick-change */}
      {locationRooms.length > 0 && <div className="flex items-center gap-1 text-caption leading-snug text-foreground-secondary">
          <Select value={appointment.roomId ?? "__no_room__"} onValueChange={(val: string) => {
        const newRoomId = val === "__no_room__" ? null : val;
        if (newRoomId !== (appointment.roomId ?? null)) {
          updateEvent.mutate({
            updates: {
              id: appointment.id,
              roomId: newRoomId
            }
          });
        }
      }}>
            <SelectTrigger className="h-6 w-fit gap-1 border-none px-1 py-0 text-caption shadow-none">
              {appointment.roomId && roomMap.get(appointment.roomId) ? <span className="flex items-center gap-1">
                  {roomMap.get(appointment.roomId)?.color && <span className="inline-block h-2 w-2 rounded-full" style={{
              backgroundColor: roomMap.get(appointment.roomId)?.color ?? undefined
            }} />}
                  {roomMap.get(appointment.roomId)?.name}
                </span> : <span className="text-foreground-muted">Assign room</span>}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__no_room__">
                <span className="text-foreground-secondary">No room</span>
              </SelectItem>
              {locationRooms.map(room => <SelectItem key={room.id} value={room.id}>
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{
                backgroundColor: room.color ?? undefined
              }} />
                    {room.name}
                  </span>
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>}

      {/* Prep Time */}
      {(() => {
      const prepSegment = getPrepSegment(appointment.segments || []);
      if (!prepSegment) return null;
      return <div className="mt-1 flex items-center gap-2">
            <PrepTimeProviderLine prepTime={prepSegment} timeZone={clock.timeZone} />
          </div>;
    })()}

      {/* Services with Colors */}
      {groupServiceAddons(getServiceSegments(appointment.segments || []), isAddonParent).map(({
      parent: service,
      addons
    }) => <div key={service.id} className="mt-1">
          <div className="flex items-center gap-2">
            {service.service?.color && <div className="h-3 w-3 flex-shrink-0 rounded-sm" style={{
          backgroundColor: service.service?.color ? service.service?.color : "#3B82F6"
        }} />}
            <div>
              <ServiceProviderLine service={service} />
            </div>
          </div>
          {addons.map(addon => <div key={addon.id} className="pl-5">
              <AddonLine addon={addon} parent={service} />
            </div>)}
        </div>)}

      {/* Cleanup Time */}
      {(() => {
      const cleanupSegment = getCleanupSegment(appointment.segments || []);
      if (!cleanupSegment) return null;
      return <div className="mt-1 flex items-center gap-2">
            <CleanupTimeProviderLine cleanupTime={cleanupSegment} timeZone={clock.timeZone} />
          </div>;
    })()}

      {/* Estimated Price */}
      {(() => {
      const serviceSegments = getServiceSegments(appointment.segments || []);
      const services = serviceSegments.map(seg => seg.service).filter(Boolean);
      const appointmentTotal = getAppointmentTotalDisplay(services, formatCurrency);
      if (!appointmentTotal) return null;
      return <div className="mt-1 text-caption leading-snug text-foreground-secondary">
            <span className="font-medium">{appointmentTotal.label}:</span>{" "}
            {appointmentTotal.total}
          </div>;
    })()}

      {/* Bookables (now separate segments at event level) */}
      {(() => {
      const bookableSegments = getBookableSegments(appointment.segments || []);
      if (bookableSegments.length === 0) return null;
      return <div className="mt-1">
            <p className="text-caption leading-snug">
              {formatList(bookableSegments.map(b => `${bookableMap.get(b.bookableId)?.name ?? "Bookable"}: ${formatTime(b.start, true, clock.timeZone)} - ${formatTime(b.end, true, clock.timeZone)}`))}
            </p>
          </div>;
    })()}

      {appointment.attribution && <div className="mt-1 flex items-center">
          <AttributionLine attribution={appointment.attribution} patientId={patient?.id} />
        </div>}

      {/* Organizer Info */}
      {appointment.organizer && <div className="mt-1 text-caption leading-snug text-foreground-secondary">
          <span>
            {appointment.organizer.type === UserType.PATIENT ? <span>{"Scheduled by patient"}</span> : <span>{`Scheduled by ${formatStaffName(appointment.organizer)}`}</span>}
            {" on "}
            <span>{formatDate(appointment.createdDate)}</span>
          </span>
        </div>}

      {/* Status - Either dropdown or text display */}
      {showStatusDropdown && onStatusChange ? <div className="mb-2 mt-3">
          {appointment.status !== EventStatus.COMPLETED && <>
              <Label>Appointment Status</Label>
              <Select value={appointment.status} onValueChange={(value: string) => onStatusChange(appointment, value as EventStatus)}>
                <SelectTrigger className="w-32 px-2 py-1 text-caption">
                  {formatToHumanReadable(appointment.status)}
                </SelectTrigger>
                <SelectContent>
                  {Object.values(EventStatus).filter(statusOption => statusOption !== EventStatus.COMPLETED || hasPermission(currentUser, PERMISSIONS.SCHEDULING_APPOINTMENTS_COMPLETE)).map(statusOption_0 => {
              return <SelectItem key={statusOption_0} value={statusOption_0}>
                          {formatToHumanReadable(statusOption_0)}
                        </SelectItem>;
            })}
                </SelectContent>
              </Select>
            </>}
        </div> : showStatus && appointment.status ? <p className={cx("mt-1 text-caption font-medium leading-snug", appointment.status === EventStatus.COMPLETED || appointment.status === EventStatus.CONFIRMED ? "text-green-500" : "text-red-500")}>
          {formatToHumanReadable(appointment.status)}
        </p> : null}

      {/* Expandable History Section */}
      <div className="mt-2">
        <CollapsibleText text="View History" collapsedText="Hide History" icon={<FiClock className="mr-1 h-3 w-3" />} open={appointment.status === EventStatus.CANCELLED}>
          <div className="mt-2 rounded-md bg-background-secondary p-3">
            <EventLogHistory eventId={appointment.id} surface="appointment_sidebar" />
          </div>
        </CollapsibleText>
      </div>

      {eventMessageTypes && eventMessageTypes.length > 0 && <div className="mt-2">
          <CollapsibleText text="View Sent Messages" collapsedText="Hide Sent Messages" icon={<FiMessageCircle className="mr-1 h-3 w-3" />}>
            <EventMessageList eventId={appointment.id} patient={patient} />
          </CollapsibleText>
        </div>}
    </div>;
}